/**
 * T6 (D9) — the two refund routes, one per target kind.
 *
 * `GET  .../refunds` → what can still be refunded on this target, what is in
 *                      flight, and whether its payment history is fully
 *                      mirrored in the ledger (`meta.coverage`).
 * `POST .../refunds` → records an EXECUTED offline refund (mode `manual`) or
 *                      asks the provider for one (mode `online`).
 *
 * A platform account may read both targets and may post to neither (P2): the
 * backend answers 403, and the UI withholds the action in the first place.
 */
import type {
  ApiPaymentRefundOptions,
  CreatePaymentRefundArgs,
  PaymentRefundTarget,
  RefundOptionsResponse,
  RefundRequestResponse,
  LedgerCoverage,
} from './types/paymentRefunds.types';
import type { BaseApiResponse } from './types/api.types';

import { post, get } from './axios';

/** Both routes hang off the same organization-scoped path. */
export function paymentRefundsUrl(target: PaymentRefundTarget) {
  return target.kind === 'membership'
    ? `/v2/organizations/${target.organizationId}/clients/${target.clientId}/memberships/${target.userMembershipId}/refunds`
    : `/v2/organizations/${target.organizationId}/bookings/${target.bookingId}/refunds`;
}

/** A stable cache discriminator for one target. */
export function paymentRefundTargetKey(
  target: PaymentRefundTarget,
): [string, ...string[]] {
  if (target.kind === 'membership') {
    return [
      target.organizationId,
      'membership',
      target.clientId,
      target.userMembershipId,
    ];
  }
  return [target.organizationId, 'booking', target.bookingId];
}

export const fetchPaymentRefundOptions = async (
  target: PaymentRefundTarget,
): Promise<ApiPaymentRefundOptions | null> => {
  const response = await get<
    BaseApiResponse<RefundOptionsResponse> & {
      meta?: { coverage?: LedgerCoverage };
    }
  >(paymentRefundsUrl(target));
  if (!response?.data) return null;
  return { ...response.data, coverage: response.meta?.coverage };
};

export const createPaymentRefund = async ({
  dto,
  target,
}: CreatePaymentRefundArgs): Promise<RefundRequestResponse | null> => {
  const response = await post<RefundRequestResponse>(
    paymentRefundsUrl(target),
    dto,
  );
  return response?.data ?? null;
};
